import moment from "moment";

/** Must match SubscriptionAccessHelper.GraceDaysAfterExpiry in the API */
export const SUBSCRIPTION_GRACE_DAYS_AFTER_EXPIRY = 3;

export const BILLING_PATH = "/administration/Billing";

const BILLING_ALLOWED_PATH_PREFIXES = [
  "/administration/billing",
  "/setting/billing",
  "/payment/stripe-return",
];

/** Routes reachable when subscription grace period has ended (billing-only mode). */
export const isBillingAllowedPath = (pathname) => {
  const path = (pathname || "").toLowerCase().replace(/\/+$/, "") || "/";
  return BILLING_ALLOWED_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
};

const BILLING_PAYMENT_PATHS = [
  "/api/payment/createstripecheckoutsession",
  "/api/payment/recordbanktransferpayment",
  "/api/payment/uploadpaymentproof",
  "/api/payment/confirmstripecheckout",
];

const isDefaultExpiryDate = (expireDate) => {
  if (!expireDate) return true;
  const m = moment(expireDate);
  return !m.isValid() || m.year() < 2000;
};

export const profileHasValidExpiry = (profileData) => {
  const expireRaw = profileData?.compExpireDate ?? profileData?.CompExpireDate ?? null;
  return !isDefaultExpiryDate(expireRaw);
};

const toDateOnly = (value) => moment(value).startOf("day");

/**
 * @param {object} profileData - Redux profile (camelCase or PascalCase)
 * @param {import('moment').Moment} [today] - optional override for tests
 */
export const evaluateSubscriptionAccess = (profileData, today = moment().startOf("day")) => {
  const empty = {
    isRestricted: false,
    isInGracePeriod: false,
    isExpired: false,
    daysUntilExpiry: Number.MAX_SAFE_INTEGER,
    daysUntilRestriction: Number.MAX_SAFE_INTEGER,
    showExpiryNotice: false,
    graceDaysAfterExpiry: SUBSCRIPTION_GRACE_DAYS_AFTER_EXPIRY,
    compExpireDate: null,
    compNumberOfDaysToShowExpiryWarning: 0,
    noticeSeverity: "info",
  };

  if (!profileData) return empty;

  const expireRaw =
    profileData.compExpireDate ?? profileData.CompExpireDate ?? null;
  if (isDefaultExpiryDate(expireRaw)) return empty;

  const warningDays =
    profileData.compNumberOfDaysToShowExpiryWarning ??
    profileData.CompNumberOfDaysToShowExpiryWarning ??
    0;

  const expiryDateOnly = toDateOnly(expireRaw);
  const graceEndDateOnly = expiryDateOnly.clone().add(SUBSCRIPTION_GRACE_DAYS_AFTER_EXPIRY, "days");
  const daysUntilExpiry = expiryDateOnly.diff(today, "days");
  const daysUntilRestriction = graceEndDateOnly.diff(today, "days");

  const isExpired = daysUntilExpiry < 0;
  const isInGracePeriod = isExpired && daysUntilRestriction > 0;
  const isRestricted = !today.isBefore(graceEndDateOnly, "day");

  const showPreExpiry =
    warningDays > 0 && daysUntilExpiry <= warningDays && daysUntilExpiry >= 0;
  const showExpiryNotice = isRestricted || isInGracePeriod || showPreExpiry;

  let noticeSeverity = "info";
  if (isRestricted) noticeSeverity = "danger";
  else if (isInGracePeriod || showPreExpiry) noticeSeverity = "warning";

  return {
    isRestricted,
    isInGracePeriod,
    isExpired,
    daysUntilExpiry,
    daysUntilRestriction,
    showExpiryNotice,
    graceDaysAfterExpiry: SUBSCRIPTION_GRACE_DAYS_AFTER_EXPIRY,
    compExpireDate: expireRaw,
    compNumberOfDaysToShowExpiryWarning: warningDays,
    noticeSeverity,
  };
};

/**
 * Prefer profile evaluation when expiry is on file; otherwise trust login/refresh API payload
 * so restricted access is not briefly cleared before profile fields are available.
 */
export const resolveSubscriptionAccess = (profileData, apiAccess) => {
  const fromProfile = evaluateSubscriptionAccess(profileData);
  const fromApi = normalizeSubscriptionAccessFromApi(apiAccess);

  if (!fromApi) return fromProfile;
  if (!profileHasValidExpiry(profileData)) return fromApi;

  return fromProfile;
};

/** Map API login/refresh SubscriptionAccess payload (PascalCase) to client shape */
export const normalizeSubscriptionAccessFromApi = (api) => {
  if (!api) return null;
  return {
    isRestricted: api.isRestricted ?? api.IsRestricted ?? false,
    isInGracePeriod: api.isInGracePeriod ?? api.IsInGracePeriod ?? false,
    isExpired: api.isExpired ?? api.IsExpired ?? false,
    daysUntilExpiry: api.daysUntilExpiry ?? api.DaysUntilExpiry ?? 0,
    daysUntilRestriction: api.daysUntilRestriction ?? api.DaysUntilRestriction ?? 0,
    showExpiryNotice: api.showExpiryNotice ?? api.ShowExpiryNotice ?? false,
    graceDaysAfterExpiry:
      api.graceDaysAfterExpiry ?? api.GraceDaysAfterExpiry ?? SUBSCRIPTION_GRACE_DAYS_AFTER_EXPIRY,
    compExpireDate: api.compExpireDate ?? api.CompExpireDate ?? null,
    compNumberOfDaysToShowExpiryWarning:
      api.compNumberOfDaysToShowExpiryWarning ?? api.CompNumberOfDaysToShowExpiryWarning ?? 0,
    noticeSeverity: api.noticeSeverity ?? api.NoticeSeverity ?? "warning",
  };
};

export const isBillingPaymentUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const path = url.split("?")[0].replace(/\/+$/, "").toLowerCase();
  return BILLING_PAYMENT_PATHS.some((p) => path.endsWith(p) || path.includes(p));
};

export const getSubscriptionNoticeMessage = (access) => {
  if (!access?.showExpiryNotice) return "";

  if (access.isRestricted) {
    return "Subscription expired. Pay your invoice to restore full access.";
  }
  if (access.isInGracePeriod) {
    const days = Math.max(1, access.daysUntilRestriction);
    const dayLabel = days === 1 ? "day" : "days";
    return `Subscription expired. ${days} ${dayLabel} left before access is limited. Pay your invoice now.`;
  }
  const n = access.daysUntilExpiry;
  if (n === 0) {
    return "Subscription will end today. Pay your invoice to avoid interruption.";
  }
  const dayLabel = n === 1 ? "day" : "days";
  return `Subscription ends in ${n} ${dayLabel}. Pay your invoice to avoid interruption.`;
};
